import { CaretRight, FunnelSimple } from "@phosphor-icons/react";
import { Fragment, type JSX, useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import { Tag } from "common/components/ui/Tag";
import { typedObjectEntries } from "common/helpers/utils";

import { ACCOUNTS_FILTERS } from "../../constants";
import AccountFilterOptions from "../AccountFilterOptions";

import type { AccountSearchParams } from "../../types";

export default function MobileFilters({
  searchParams,
  setSearchParams,
  clearSearchParamsButton,
}: {
  searchParams: AccountSearchParams;
  setSearchParams: (searchParams: AccountSearchParams) => void;
  clearSearchParamsButton: JSX.Element;
}) {
  const [selectedKey, setSelectedKey] = useState<keyof AccountSearchParams>();

  let content = (
    <>
      <DrawerHeader
        title="Filter account items"
        actionComponent={clearSearchParamsButton}
      />
      {typedObjectEntries(ACCOUNTS_FILTERS).map(
        <SearchParamsKey extends keyof AccountSearchParams>([
          key,
          { icon, label },
        ]: [SearchParamsKey, (typeof ACCOUNTS_FILTERS)[SearchParamsKey]]) => {
          return (
            <Fragment key={key}>
              <button
                className="px-2 py-2.5 flex items-center gap-x-1.5 text-button-sm [&_svg:first-child]:size-5"
                type="button"
                onClick={() => setSelectedKey(key)}
              >
                {icon}
                {label}
                <RenderIf condition={!!searchParams[key].length}>
                  <Tag size="sm">{searchParams[key].length}</Tag>
                </RenderIf>
                <CaretRight className="ml-auto size-4" />
              </button>
              <div className="h-px my-1 bg-black-200 last:hidden" />
            </Fragment>
          );
        },
      )}
    </>
  );

  if (selectedKey) {
    const { label } = ACCOUNTS_FILTERS[selectedKey];
    content = (
      <>
        <DrawerHeader
          title={label}
          onBack={() => setSelectedKey(undefined)}
          actionComponent={
            <RenderIf condition={!!searchParams[selectedKey].length}>
              <Button
                variant="link-danger"
                onClick={() =>
                  setSearchParams({ ...searchParams, [selectedKey]: [] })
                }
              >
                Clear
              </Button>
            </RenderIf>
          }
        />
        <div className="flex flex-col gap-y-1">
          <AccountFilterOptions
            searchParamsKey={selectedKey}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        </div>
      </>
    );
  }

  return (
    <Drawer onAfterClose={() => setSelectedKey(undefined)}>
      <DrawerTrigger asChild>
        <Button size="lg" intent="iconOnly" variant="outline-black">
          <FunnelSimple />
        </Button>
      </DrawerTrigger>
      <DrawerContent>{content}</DrawerContent>
    </Drawer>
  );
}
